import { useState, type FormEvent, type ReactNode } from 'react';
import { Card, ErrorBanner, Loading, Spinner } from '../../components/ui.js';
import { api } from '../../lib/api.js';
import { formatDate } from '../../lib/format.js';
import { useAction, useAsync } from '../../lib/hooks.js';
import { AdminLayout } from './AdminLayout.js';

interface Release {
  platform: string;
  latestBuild: number;
  latestDisplayVersion: string;
  minimumBuild: number;
  storeUrl: string;
  releaseNotes: string | null;
  updatedAt: string;
}

interface ReleaseResponse {
  data: Release[];
  platforms: string[];
}

/**
 * What each platform should be running.
 *
 * The screen is shaped around the one dangerous field. `minimumBuild` is what
 * locks people out: raise it and every older build is refused entry until its
 * user visits the store. So it is labelled as such, warned about before saving,
 * and the consequence is spelled out next to the input rather than left in
 * documentation nobody opens.
 */
export function AdminReleasesPage(): ReactNode {
  return (
    <AdminLayout>{(staff) => <Releases canWrite={staff.role === 'superadmin'} />}</AdminLayout>
  );
}

/** A friendlier name than the enum value. */
const PLATFORM_LABELS: Record<string, string> = {
  android: 'Android',
  ios: 'iOS',
  windows: 'Windows',
  maccatalyst: 'macOS',
};

function Releases({ canWrite }: { canWrite: boolean }): ReactNode {
  const releases = useAsync(() => api.get<ReleaseResponse>('/api/admin/app-releases'), []);
  const { run, pending, error } = useAction();
  const [editing, setEditing] = useState<string | null>(null);

  if (releases.loading) return <Loading />;

  const platforms = releases.data?.platforms ?? [];
  const list = releases.data?.data ?? [];
  const byPlatform = new Map(list.map((r) => [r.platform, r]));

  async function remove(platform: string): Promise<void> {
    await run(async () => {
      await api.delete(`/api/admin/app-releases/${platform}`);
      releases.reload();
    });
  }

  return (
    <div className="stack">
      <ErrorBanner error={releases.error} />
      <ErrorBanner message={error} />

      <div className="banner">
        A platform with no release configured never prompts anybody — the check answers “up to
        date”. That is also how you recover if a minimum build is set too high: remove the row.
      </div>

      {platforms.map((platform) => {
        const release = byPlatform.get(platform);
        const locksOut = release ? release.minimumBuild > 1 : false;

        return (
          <Card key={platform}>
            <div className="row">
              <div>
                <div className="row" style={{ gap: '0.5rem' }}>
                  <strong>{PLATFORM_LABELS[platform] ?? platform}</strong>
                  {release ? (
                    <>
                      <span className="badge">{release.latestDisplayVersion}</span>
                      <span className="badge">build {release.latestBuild}</span>
                      {locksOut && (
                        <span className="badge badge-warning">
                          blocks below {release.minimumBuild}
                        </span>
                      )}
                    </>
                  ) : (
                    <span className="badge">not configured</span>
                  )}
                </div>
                {release && (
                  <p className="small faint" style={{ marginTop: '0.3rem' }}>
                    Updated {formatDate(release.updatedAt)}
                  </p>
                )}
              </div>

              {canWrite && (
                <div className="button-row" style={{ marginLeft: 'auto' }}>
                  <button
                    type="button"
                    className="button-secondary"
                    disabled={pending}
                    onClick={() => setEditing(editing === platform ? null : platform)}
                  >
                    {release ? 'Edit' : 'Configure'}
                  </button>
                  {release && (
                    <button
                      type="button"
                      className="button-danger"
                      disabled={pending}
                      onClick={() => void remove(platform)}
                    >
                      Stop gating
                    </button>
                  )}
                </div>
              )}
            </div>

            {release && (
              <>
                <table style={{ marginTop: '0.75rem' }}>
                  <tbody>
                    <tr>
                      <td className="muted small">Latest build</td>
                      <td className="mono">
                        {release.latestBuild} ({release.latestDisplayVersion})
                      </td>
                    </tr>
                    <tr>
                      <td className="muted small">Minimum build</td>
                      <td className="mono">{release.minimumBuild}</td>
                    </tr>
                    <tr>
                      <td className="muted small">Store link</td>
                      <td className="mono small">{release.storeUrl}</td>
                    </tr>
                  </tbody>
                </table>
                {release.releaseNotes && (
                  <p className="small" style={{ marginTop: '0.6rem' }}>
                    {release.releaseNotes}
                  </p>
                )}
              </>
            )}

            {editing === platform && (
              <ReleaseForm
                platform={platform}
                existing={release}
                onDone={() => {
                  setEditing(null);
                  releases.reload();
                }}
                onCancel={() => setEditing(null)}
              />
            )}
          </Card>
        );
      })}
    </div>
  );
}

/** Sensible starting points, so nobody has to look them up. */
const STORE_URL_HINTS: Record<string, string> = {
  android: 'https://play.google.com/store/apps/details?id=in.rioplay.live',
  ios: 'https://apps.apple.com/app/id0000000000',
  windows: 'ms-windows-store://pdp/?productid=9NBCLC80W14N',
  maccatalyst: 'https://apps.apple.com/app/id0000000000',
};

function ReleaseForm({
  platform,
  existing,
  onDone,
  onCancel,
}: {
  platform: string;
  existing: Release | undefined;
  onDone: () => void;
  onCancel: () => void;
}): ReactNode {
  const [latestBuild, setLatestBuild] = useState(String(existing?.latestBuild ?? ''));
  const [latestDisplayVersion, setLatestDisplayVersion] = useState(
    existing?.latestDisplayVersion ?? '',
  );
  const [minimumBuild, setMinimumBuild] = useState(String(existing?.minimumBuild ?? '1'));
  const [storeUrl, setStoreUrl] = useState(existing?.storeUrl ?? STORE_URL_HINTS[platform] ?? '');
  const [releaseNotes, setReleaseNotes] = useState(existing?.releaseNotes ?? '');
  const { run, pending, error } = useAction();

  const latest = Number(latestBuild);
  const minimum = Number(minimumBuild);

  /*
   * Shown before saving rather than reported afterwards. The server refuses this
   * and so does a database constraint, but an operator should not have to submit
   * a form to find out they were about to lock everybody out.
   */
  const invalid = Boolean(latestBuild && minimumBuild && minimum > latest);
  const raisesFloor = Boolean(existing && minimum > existing.minimumBuild);

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    await run(async () => {
      await api.put(`/api/admin/app-releases/${platform}`, {
        latestBuild: latest,
        latestDisplayVersion,
        minimumBuild: minimum,
        storeUrl,
        releaseNotes: releaseNotes.trim() || null,
      });
      onDone();
    });
  }

  return (
    <form onSubmit={(e) => void submit(e)} className="stack" style={{ marginTop: '0.75rem' }}>
      <ErrorBanner message={error} />

      <div className="row" style={{ alignItems: 'flex-end' }}>
        <div style={{ flex: 1 }}>
          <label htmlFor={`latest-${platform}`}>Latest build</label>
          <input
            id={`latest-${platform}`}
            type="number"
            min="1"
            value={latestBuild}
            onChange={(e) => setLatestBuild(e.target.value)}
            required
          />
          <p className="small faint" style={{ marginTop: '0.3rem' }}>
            The integer build number, not the version name. Android’s versionCode, iOS’s build,
            the third field of the MSIX version.
          </p>
        </div>
        <div style={{ flex: 1 }}>
          <label htmlFor={`display-${platform}`}>Version name</label>
          <input
            id={`display-${platform}`}
            value={latestDisplayVersion}
            onChange={(e) => setLatestDisplayVersion(e.target.value)}
            placeholder="1.0.43"
            required
          />
          <p className="small faint" style={{ marginTop: '0.3rem' }}>
            Shown to the user. Never compared.
          </p>
        </div>
      </div>

      <div>
        <label htmlFor={`minimum-${platform}`}>Minimum build — anything older is blocked</label>
        <input
          id={`minimum-${platform}`}
          type="number"
          min="1"
          value={minimumBuild}
          onChange={(e) => setMinimumBuild(e.target.value)}
          required
        />
        <p className="small faint" style={{ marginTop: '0.3rem' }}>
          Users on an older build cannot use the app until they update. Leave it at 1 to prompt
          without forcing. Set it equal to the latest build only when the older ones are genuinely
          broken.
        </p>
      </div>

      {invalid && (
        <div className="banner banner-danger">
          The minimum build cannot be above the latest build — that would block every user,
          including anyone who just updated, with no way to recover from inside the app.
        </div>
      )}

      {raisesFloor && !invalid && (
        <div className="banner banner-warning">
          This raises the minimum from {existing?.minimumBuild} to {minimum}. Everyone on a build
          below {minimum} will be locked out until they update, so make sure that build is actually
          live in the store first.
        </div>
      )}

      <div>
        <label htmlFor={`store-${platform}`}>Store link</label>
        <input
          id={`store-${platform}`}
          value={storeUrl}
          onChange={(e) => setStoreUrl(e.target.value)}
          required
        />
        <p className="small faint" style={{ marginTop: '0.3rem' }}>
          Prefer a deep link that opens the store app directly, so the user lands on the install
          button rather than a web page.
        </p>
      </div>

      <div>
        <label htmlFor={`notes-${platform}`}>Release notes (optional)</label>
        <input
          id={`notes-${platform}`}
          value={releaseNotes}
          onChange={(e) => setReleaseNotes(e.target.value)}
          placeholder="Fixes joining on slow connections."
        />
      </div>

      <div className="button-row">
        <button type="submit" className="button-primary" disabled={pending || invalid}>
          {pending && <Spinner />}
          Save
        </button>
        <button type="button" className="button-secondary" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}
